import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { skills } from '../data/portfolio';
import { Cpu, Server, Smartphone, Cloud, Shield } from 'lucide-react';
import ParticleField from './ParticleField';
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: 'easeOut' }
  })
};

const iconMap = {
  cpu: Cpu, server: Server, smartphone: Smartphone, cloud: Cloud, shield: Shield
};

const colorMap = {
  primary: { bg: 'hsl(var(--primary) / 0.1)', border: 'hsl(var(--primary) / 0.25)', text: 'hsl(var(--primary))', bar: 'hsl(var(--primary))' },
  secondary: { bg: 'hsl(var(--secondary) / 0.1)', border: 'hsl(var(--secondary) / 0.25)', text: 'hsl(var(--secondary))', bar: 'hsl(var(--secondary))' },
  accent: { bg: 'hsl(var(--accent) / 0.1)', border: 'hsl(var(--accent) / 0.25)', text: 'hsl(var(--accent))', bar: 'hsl(var(--accent))' }
};

const SkillBar = ({ name, level, color, index }) => {
  const c = colorMap[color];
  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      className="space-y-1.5"
    >
      <div className="flex justify-between items-center">
        <span className="text-foreground text-sm font-medium">{name}</span>
        <span className="text-muted-foreground text-xs font-mono">{level}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: `${level}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: index * 0.05, ease: 'easeOut' }}
          style={{ background: c.bar }}
        />
      </div>
    </motion.div>
  );
};

const SkillsSection = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const active = skills[activeCategory];
  const Icon = iconMap[active.icon];
  const c = colorMap[active.color];

  // All unique tech tags
  const allTags = skills.flatMap(s => s.items.map(i => i.name));

  return (
    <section id="skills" className="relative py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div
        className="absolute inset-0 bg-grid opacity-20 pointer-events-none"
      />
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-[400px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.05) 0%, transparent 70%)' }}
      />

      <ParticleField />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Skills
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Technical
            <span className="gradient-text"> Arsenal</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl">
            A comprehensive toolkit built over 7+ years of engineering across AI, automation, frontend, backend, CI CD, and cloud.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Category sidebar */}
          <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {skills.map((s, i) => {
              const CatIcon = iconMap[s.icon];
              const isActive = i === activeCategory;
              const cc = colorMap[s.color];
              return (
                <motion.button
                  key={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  custom={i}
                  viewport={{ once: true }}
                  onClick={() => setActiveCategory(i)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-left whitespace-nowrap lg:whitespace-normal transition-all duration-200 flex-shrink-0 lg:flex-shrink"
                  style={{
                    background: isActive ? cc.bg : 'transparent',
                    border: `1px solid ${isActive ? cc.border : 'hsl(var(--border))'}`,
                    color: isActive ? cc.text : 'hsl(var(--muted-foreground))'
                  }}
                >
                  <CatIcon size={16} style={{ flexShrink: 0 }} />
                  <span className="text-sm font-medium">{s.category}</span>
                </motion.button>
              );
            })}
          </div>

          {/* Skills detail */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="glass rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-8">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: c.bg, border: `1px solid ${c.border}` }}
              >
                <Icon size={20} style={{ color: c.text }} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">{active.category}</h3>
                <p className="text-muted-foreground text-xs">{active.items.length} technologies</p>
              </div>
            </div>

            <motion.div
              variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
              initial="hidden"
              animate="visible"
              className="space-y-5"
            >
              {active.items.map((item, idx) => (
                <SkillBar
                  key={item.name}
                  name={item.name}
                  level={item.level}
                  color={active.color}
                  index={idx}
                />
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Tag cloud */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 pt-12 border-t border-border/50"
        >
          <p className="text-muted-foreground text-sm mb-6 font-mono">// all technologies</p>
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag, i) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.02, duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="text-xs font-mono px-3 py-1.5 rounded-full border border-border/60 text-muted-foreground cursor-default hover:border-primary/40 hover:text-primary transition-colors duration-200"
              >
                {tag}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SkillsSection;
